import type { EmergencyCall } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCalls.types';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import { formatAddressFromObject } from '../../../../AirAmbulanceForm/AirAmbulanceForm/utils';

interface MostRecentLocation {
  latitude: number;
  longitude: number;
  addressObject?: Record<string, string | undefined>;
  rgAddress?: string;
}

export interface CallLocation {
  latitude: number;
  longitude: number;
  address: string;
}

const MIN_LOOKUP_DIGITS = 10;

function digitsOf(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Most recent location of a call, with its address formatted for the form.
 * Returns null when the call has no usable location.
 */
export function getCallLocation(
  call: EmergencyCall | null | undefined,
): CallLocation | null {
  if (!call) {
    return null;
  }
  const latestLocation = getMostRecentLoc(call) as MostRecentLocation | null;
  if (!latestLocation) {
    return null;
  }

  return {
    latitude: latestLocation.latitude,
    longitude: latestLocation.longitude,
    address: latestLocation.addressObject
      ? formatAddressFromObject(latestLocation.addressObject)
      : (latestLocation.rgAddress ?? ''),
  };
}

/**
 * Find a call in the queue by a typed phone number. The queue is keyed by
 * caller ID, so an exact key match wins; otherwise numbers are compared on
 * digits only, tolerating a missing country code (e.g. a typed 10-digit US
 * number matches its +1-prefixed caller ID).
 */
export function findCallByPhoneNumber(
  callQueue: Record<string, EmergencyCall>,
  phoneNumber: string,
): EmergencyCall | null {
  if (callQueue[phoneNumber]) {
    return callQueue[phoneNumber];
  }

  const typedDigits = digitsOf(phoneNumber);
  if (typedDigits.length < MIN_LOOKUP_DIGITS) {
    return null;
  }

  for (const [callerId, call] of Object.entries(callQueue)) {
    const callerDigits = digitsOf(callerId);
    if (
      callerDigits === typedDigits ||
      callerDigits.endsWith(typedDigits) ||
      typedDigits.endsWith(callerDigits)
    ) {
      return call;
    }
  }

  return null;
}
